import { cloneDeep, isEmpty, merge, mergeWith, uniqueId } from 'lodash-es'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { ExternalMedia } from '@/composables/use-file-uploads/types/models/UploadableFile.ts'
import type { ProductModel, TempProductModel } from '@/domains/products/types/models/ProductModel.types.ts'
import type {
  ProductEnhanceRequest,
  ProductEnhanceResponse
} from '@/domains/products/types/requests/ProductRequests.types.ts'
import type { FlatLocaleObject, ReplacementsMap } from '@/locales/useLocales.ts'

import { useToastStore } from '@/domains/notifications/store/use-toast-store/useToastStore.ts'
import { callProducts } from '@/domains/products/services'
import { useProductVariantStore } from '@/domains/products/store/use-product-variant-store/useProductVariantStore.ts'
import { useLocales } from '@/locales/useLocales.ts'
import { deepCleanNulls } from '@/utils/data-utils/dataUtils.ts'
import { generateComplexId } from '@/utils/math-util/mathUtil.ts'

// Define the store for managing barcode-related data
export const useProductEnhancerStore = defineStore('productEnhancer', () => {
  const productVariantStore = useProductVariantStore()
  const toastStore = useToastStore()
  const locales = useLocales()

  // State
  const result = ref<ProductEnhanceResponse>({} as ProductEnhanceResponse)
  const isBusy = ref(false)

  // Computed
  const enhancedRawProduct = computed(() => result.value.product)
  const enhanceProductLocales = locales.quickDictionary.value.barcodeToProduct
  /**
   * Normalizes variant type IDs across product variants
   *
   * This ensures consistent IDs for the same variant type names
   * @param product The product to normalize
   * @returns The normalized product
   */
  const normalizeVariantTypeIds = (product: ProductModel): ProductModel => {
    if (!product.variants || product.variants.length === 0) {
      return product
    }

    // Apply default values to all variants
    const defaultVariant = productVariantStore.defaults.variant()
    product.variants = product.variants.map(variant => mergeWith({}, defaultVariant, variant))

    // Build a map of variant type names to their IDs
    const variantTypeMap: Record<string, number> = {}

    // Step 1: Extract known variant type IDs
    product.variants.forEach(variant => {
      variant.variant_type_options?.forEach(option => {
        const { name } = option.variant_type
        const id = option.variant_type?.id

        if (name && id !== null && id !== undefined && !variantTypeMap[name]) {
          variantTypeMap[name] = id
        }
      })
    })

    // Step 2: If map is empty, skip processing
    if (Object.keys(variantTypeMap).length === 0) {
      return product
    }

    // Step 3: Assign matching IDs to unnamed variant types
    product.variants.forEach(variant => {
      variant.variant_type_options?.forEach(option => {
        const { name } = option.variant_type

        if (name && (option.variant_type.id === null || option.variant_type.id === undefined)) {
          if (variantTypeMap[name]) {
            option.variant_type.id = variantTypeMap[name]
          }
        }
      })
    })

    return product
  }

  /**
   * Normalized product data with consistent variant types and merged media
   */
  const normalizedResult = computed(() => {
    if (isEmpty(result.value)) {
      return undefined
    }

    const product = enhancedRawProduct.value

    // Create a set of unique external media URLs
    const distinctExternalMedia = new Set(product?.external_data?.media || [])

    // Convert external media URLs to ExternalMedia objects
    const externalMediaAsExternalURL: ExternalMedia[] = ([...distinctExternalMedia] as string[]).map(entry => ({
      file_name: uniqueId('proposed-image'),
      type: 'URL',
      url: entry,
      status: null
    }))

    // Create a normalized copy of the product
    const normalizedProduct = normalizeVariantTypeIds({ ...product })

    // Merge existing media with external media
    normalizedProduct.media = merge([], normalizedProduct.media || [], externalMediaAsExternalURL)

    return {
      ...result.value,
      product: normalizedProduct
    }
  })

  // Show Notifications for Unhandled products
  const notifyUnhandledProducts = (operationId: string) => {
    if (result.value.unrelated_products?.length) {
      result.value.unrelated_products.forEach(_unrelatedProduct => {
        const details: FlatLocaleObject = useLocales().localesHelper(enhanceProductLocales.toast.unrelated, {
          detail: [_unrelatedProduct.barcode]
        } as ReplacementsMap) as FlatLocaleObject

        toastStore.add({
          id: operationId,
          state: 'warn',
          ...details
        })
      })
    }

    if (result.value.failed_barcodes?.length) {
      result.value.failed_barcodes.forEach(_barcode => {
        const details: FlatLocaleObject = useLocales().localesHelper(enhanceProductLocales.toast.notFound, {
          detail: [_barcode]
        } as ReplacementsMap) as FlatLocaleObject

        toastStore.add({
          id: operationId,
          state: 'warn',
          ...details
        })
      })
    }
  }

  const handleSuccessfullProduct = (operationId: string, product: TempProductModel) => {
    if (product.title && product.description) {
      toastStore.add({
        id: operationId,
        state: 'success'
      })
    }
  }

  // Actions
  const enhance = async (payload: ProductEnhanceRequest) => {
    toggleBusy()

    const operationId = generateComplexId()

    toastStore.add({
      id: operationId,
      state: 'init',
      severity: 'info'
    })

    const strippedPayload = cloneDeep(payload)

    if (strippedPayload.product) {
      strippedPayload.product.media = []
    }

    try {
      const response = await callProducts().enhance(strippedPayload)

      handleSuccessfullProduct(operationId, response.data.value?.product || ({} as TempProductModel))
      result.value = deepCleanNulls(response.data.value) as ProductEnhanceResponse
      notifyUnhandledProducts(operationId)

      return response
    } catch (error) {
      toastStore.add({
        severity: 'error',
        id: operationId,
        state: 'error'
      })
      throw error
    } finally {
      toggleBusy()
    }
  }

  const toggleBusy = () => {
    isBusy.value = !isBusy.value
  }

  // Function to reset the store by clearing all barcode entries
  const resetStore = () => {
    result.value = {} as ProductEnhanceResponse
    isBusy.value = false
  }

  // Return all the properties and functions to be used in the components
  return {
    result,
    normalizedResult,
    enhance,
    resetStore,
    isBusy,
    toggleBusy
  }
})
